/**
 * Colour tokens of the V2 design system (plan §4.1). Plain ESM: imported at
 * build time by tailwind.config.js and at run time by src/plugins/vuetify.js.
 *
 * Every role palette is backed by CSS variables whose light values are
 * Tailwind's own, so existing classes (`tw-bg-neutral-100`…) keep rendering
 * the same colours. A future dark mode only redefines the variables under
 * `:root[data-theme="dark"]`.
 */

/** Role colour scales: variable-backed Tailwind palettes (light values = Tailwind's defaults). */
export const ROLE_SCALES = [
  'neutral',
  'orange',
  'emerald',
  'amber',
  'sky',
  'red',
];

/** Semantic tokens whose role a palette step cannot express. Light values. */
export const SEMANTIC_LIGHT = {
  /** Page background behind cards. */
  canvas: '#f5f5f5',
  /** Cards, bars, drawers, sheets, inputs, popovers. */
  surface: '#ffffff',
  /** Text on brand/status solid fills (stays white in dark mode). */
  'on-color': '#ffffff',
  /** Text on neutral-700/800/900 fills (flips in dark mode). */
  'on-inverse': '#ffffff',
  /** Scrims, always used with an opacity modifier (stays dark in dark mode). */
  scrim: '#171717',
};

/** '#f5f5f5' → '245 245 245' (the `rgb(var(--c-x) / a)` channel form). */
export function hexToChannels(hex) {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full))
    throw new Error(`Invalid hex colour ${hex}`);

  return [0, 2, 4]
    .map((start) => parseInt(full.slice(start, start + 2), 16))
    .join(' ');
}

/**
 * `{ '--c-neutral-50': '250 250 250', …, '--c-canvas': '245 245 245', … }`:
 * every step of every role scale of `palette` (Tailwind's colours) plus the
 * semantic tokens.
 */
export function buildThemeCssVars(palette, semantic) {
  const vars = {};
  for (const scale of ROLE_SCALES)
    for (const [step, hex] of Object.entries(palette[scale]))
      vars[`--c-${scale}-${step}`] = hexToChannels(hex);
  for (const [name, hex] of Object.entries(semantic))
    vars[`--c-${name}`] = hexToChannels(hex);
  return vars;
}

/** Tailwind colour value backed by a token variable (keeps opacity modifiers working). */
export const colorVar = (name) => `rgb(var(--c-${name}) / <alpha-value>)`;
